import { Trans, t } from "@lingui/macro";
import cx from "classnames";
import { useCallback, useMemo, useState } from "react";

import { Link } from "react-router-dom";
import { useDebounce } from "lib/useDebounce";
import { shortenAddress } from "lib/legacy";

import { BottomTablePagination } from "components/Pagination/BottomTablePagination";
import { TraderAvatar } from "./TraderAvatar";
import SearchInput from "components/SearchInput/SearchInput";
import { TableTd, TableTh, TableTheadTr, TableTr } from "components/Table/Table";
import { TableScrollFadeContainer } from "components/TableScrollFade/TableScrollFade";

import type { LeaderboardSort, TraderProfile } from "domain/copyTrading/types";

const PER_PAGE = 20;

type SortDir = "asc" | "desc";

type Props = {
  traders: TraderProfile[];
  isLoading: boolean;
  followingSet: Set<string>;
  onFollow: (trader: TraderProfile) => void;
  onUnfollow: (address: string) => void;
  currentAccount?: string;
};

// Adaptive USD formatting: keep cents for small amounts instead of rounding them
// to $0, and only switch to K/M once the magnitude actually warrants it.
function formatUsdCompact(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(2)}`;
}

function PnlCell({ pnl, pct }: { pnl?: bigint; pct?: number }) {
  const hasData = pnl !== undefined && pct !== undefined;
  if (!hasData) return <span className="CT-muted">—</span>;

  const pos = pnl >= 0n;
  const abs = pos ? pnl : -pnl;
  const absNum = Number(abs) / 1e30;
  const formatted = `${pos ? "+" : "-"}${formatUsdCompact(absNum)}`;

  return (
    <div className="CT-pnl-cell">
      <span className={pos ? "CT-positive" : "CT-negative"}>{formatted}</span>
      <span className={cx("CT-pnl-pct", pos ? "CT-positive" : "CT-negative")}>
        {pos ? "+" : ""}
        {pct?.toFixed(1)}%
      </span>
    </div>
  );
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <span className="CT-rank CT-rank--gold">1</span>;
  if (rank === 2) return <span className="CT-rank CT-rank--silver">2</span>;
  if (rank === 3) return <span className="CT-rank CT-rank--bronze">3</span>;
  return <span className="CT-rank">{rank}</span>;
}

function FollowButton({
  isFollowing,
  onFollow,
  onUnfollow,
  disabled,
}: {
  isFollowing: boolean;
  onFollow: () => void;
  onUnfollow: () => void;
  disabled?: boolean;
}) {
  const [hovered, setHovered] = useState(false);

  if (isFollowing) {
    return (
      <button
        className={cx("CT-btn-follow CT-btn-follow--active", { "CT-btn-follow--unfollow": hovered })}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={onUnfollow}
      >
        {hovered ? <Trans>Unfollow</Trans> : <Trans>✓ Following</Trans>}
      </button>
    );
  }

  return (
    <button className="CT-btn-follow" onClick={onFollow} disabled={disabled}>
      <Trans>Follow</Trans>
    </button>
  );
}

function sortTraders(traders: TraderProfile[], field: LeaderboardSort, dir: SortDir): TraderProfile[] {
  return [...traders].sort((a, b) => {
    let va = 0;
    let vb = 0;
    if (field === "pnl") {
      va = Number(a.pnl30d ?? 0n) / 1e30;
      vb = Number(b.pnl30d ?? 0n) / 1e30;
    } else if (field === "followers") {
      va = a.followerCount;
      vb = b.followerCount;
    } else if (field === "winRate") {
      va = a.winRate ?? 0;
      vb = b.winRate ?? 0;
    } else if (field === "fee") {
      va = a.copyFeeRate;
      vb = b.copyFeeRate;
    } else if (field === "volume") {
      va = Number(a.totalVolume ?? 0n) / 1e30;
      vb = Number(b.totalVolume ?? 0n) / 1e30;
    }
    return dir === "desc" ? vb - va : va - vb;
  });
}

export function TraderLeaderboard({ traders, isLoading, followingSet, onFollow, onUnfollow, currentAccount }: Props) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<LeaderboardSort>("followers");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const debouncedSearch = useDebounce(search, 300);

  const handleSort = useCallback(
    (field: LeaderboardSort) => {
      if (sortField === field) {
        setSortDir((d) => (d === "desc" ? "asc" : "desc"));
      } else {
        setSortField(field);
        setSortDir("desc");
      }
      setPage(1);
    },
    [sortField]
  );

  const sortedFiltered = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    const filtered = q ? traders.filter((t) => t.address.toLowerCase().includes(q)) : traders;
    return sortTraders(filtered, sortField, sortDir);
  }, [traders, debouncedSearch, sortField, sortDir]);

  const pageCount = Math.max(1, Math.ceil(sortedFiltered.length / PER_PAGE));
  const pageItems = sortedFiltered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  function SortTh({ field, children }: { field: LeaderboardSort; children: React.ReactNode }) {
    const active = sortField === field;
    return (
      <TableTh
        className={cx("CT-th-sort", { "CT-th-sort--active": active })}
        onClick={() => handleSort(field)}
      >
        <span className="CT-th-sort__inner">
          {children}
          <span className="CT-sort-arrow">{active ? (sortDir === "desc" ? " ↓" : " ↑") : " ↕"}</span>
        </span>
      </TableTh>
    );
  }

  if (isLoading) {
    return (
      <div className="CT-loading">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="CT-skeleton-row" />
        ))}
      </div>
    );
  }

  return (
    <div className="CT-leaderboard">
      <div className="CT-leaderboard__toolbar">
        <SearchInput
          value={search}
          setValue={(v) => {
            setSearch(v);
            setPage(1);
          }}
          placeholder={t`Search trader address...`}
          className="CT-search"
        />
        <span className="CT-leaderboard__count">
          {sortedFiltered.length} <Trans>traders</Trans>
        </span>
      </div>

      <TableScrollFadeContainer>
        <table className="CT-table w-full rounded-4 bg-slate-800">
          <thead>
            <TableTheadTr bordered>
              <TableTh>
                <Trans>#</Trans>
              </TableTh>
              <TableTh>
                <Trans>Trader</Trans>
              </TableTh>
              <SortTh field="pnl">
                <Trans>30d PnL</Trans>
              </SortTh>
              <SortTh field="winRate">
                <Trans>Win Rate</Trans>
              </SortTh>
              <SortTh field="volume">
                <Trans>Volume</Trans>
              </SortTh>
              <SortTh field="followers">
                <Trans>Followers</Trans>
              </SortTh>
              <SortTh field="fee">
                <Trans>Fee</Trans>
              </SortTh>
              <TableTh>
                <Trans>Action</Trans>
              </TableTh>
            </TableTheadTr>
          </thead>
          <tbody>
            {pageItems.length === 0 ? (
              <tr>
                <td colSpan={8} className="CT-empty">
                  <Trans>No traders found</Trans>
                </td>
              </tr>
            ) : (
              pageItems.map((trader, i) => {
                const globalRank = (page - 1) * PER_PAGE + i + 1;
                const isFollowing = followingSet.has(trader.address.toLowerCase());
                const isSelf = currentAccount?.toLowerCase() === trader.address.toLowerCase();
                return (
                  <TableTr key={trader.address} hoverable>
                    <TableTd padding="compact">
                      <RankBadge rank={globalRank} />
                    </TableTd>
                    <TableTd padding="compact">
                      <Link to={`/copytrading/trader/${trader.address}`} className="CT-trader-link CT-trader-id">
                        <TraderAvatar
                          address={trader.address}
                          avatarUrl={trader.avatarUrl}
                          name={trader.displayName}
                          size={32}
                        />
                        <span className="CT-trader-id__text">
                          {trader.displayName ? (
                            <>
                              <span className="CT-trader-id__name">{trader.displayName}</span>
                              <span className="CT-trader-id__addr">{shortenAddress(trader.address, 13)}</span>
                            </>
                          ) : (
                            <span className="CT-trader-id__addr">{shortenAddress(trader.address, 13)}</span>
                          )}
                        </span>
                      </Link>
                    </TableTd>
                    <TableTd padding="compact">
                      <PnlCell pnl={trader.pnl30d} pct={trader.pnlPercent30d} />
                    </TableTd>
                    <TableTd padding="compact">
                      {trader.winRate !== undefined ? (
                        <span className={trader.winRate >= 50 ? "CT-positive" : "CT-muted"}>
                          {trader.winRate.toFixed(0)}%
                        </span>
                      ) : (
                        <span className="CT-muted">—</span>
                      )}
                    </TableTd>
                    <TableTd padding="compact">
                      {trader.totalVolume !== undefined ? (
                        <span>{formatUsdCompact(Number(trader.totalVolume) / 1e30)}</span>
                      ) : (
                        <span className="CT-muted">—</span>
                      )}
                    </TableTd>
                    <TableTd padding="compact">
                      <span className="CT-followers">
                        {trader.followerCount.toLocaleString()}
                      </span>
                    </TableTd>
                    <TableTd padding="compact">
                      <span className={trader.copyFeeRate === 0 ? "CT-positive" : "CT-fee"}>
                        {trader.copyFeeRate === 0 ? "Free" : `${(trader.copyFeeRate / 100).toFixed(1)}%`}
                      </span>
                    </TableTd>
                    <TableTd padding="compact">
                      {isSelf ? (
                        <span className="CT-badge-self">
                          <Trans>You</Trans>
                        </span>
                      ) : (
                        <FollowButton
                          isFollowing={isFollowing}
                          onFollow={() => onFollow(trader)}
                          onUnfollow={() => onUnfollow(trader.address)}
                        />
                      )}
                    </TableTd>
                  </TableTr>
                );
              })
            )}
          </tbody>
        </table>
      </TableScrollFadeContainer>

      <BottomTablePagination page={page} pageCount={pageCount} onPageChange={setPage} />
    </div>
  );
}
